// Plan studio detail. Contract for the endpoint another agent is adding:
//
//   GET /api/marketing/plans/:planId/detail?brandId=&revision=
//
// The wire shape below is what this adapter reads. Callers render
// PlanStudio and do not parse the JSON themselves. A missing endpoint
// (404/405/501) is an empty detail, not a crash. Account ids, tokens,
// and feed keys are never copied onto the view. Plan ids are for the
// request only and are not part of the view.
//
// {
//   title, status, revision, currency,
//   structure: [{ name, status, source, adSets: [{
//     name, status, source,
//     targeting: { geo, ages, gender, exclusions, advantageAudience, placements },
//     ads: [{ name, status, source, creatives: [{
//       name, type, colorway, exists, source, copyApproval, reachSocialCreated
//     }] }]
//   }] }],
//   creatives: [{ name, type, colorway, exists, source, copyApproval, reachSocialCreated }],
//   budget: {
//     weekly, flight,
//     lines: [{ name, weekly, flight }],
//     tests: [{ name, weekly, flight }]
//   },
//   forecast: {
//     total: { spend, purchases, cpa, roas, revenue, incrementalRevenue },
//     method
//   },
//   coverage: {
//     unavailableReason,
//     gaps: [{ line, required, missing, creativeType, products }]
//   },
//   suggestions: [{
//     tool, args, action, estimatedCostUsd,
//     productIds, products: [{ title, productId }],
//     campaignId, kinds, planLineKey, creativeType,
//     handoff: { productIds, units, campaignId }
//   }]
// }
//
// `handoff.productIds` are real ids. `handoff.units` is the exact
// generate count when it is a positive number, or the count of units
// when it is a list. A suggestion with no campaignId uses the plan
// line's campaign when that line is present once. creativeType is
// static-ad, video-ad, or catalog-image.
//
// `structure` is the campaign array. Each campaign has `action`
// (create, mirrorNew, reuse), and `statusReason` is shown when `status`
// is null. An ad's `creativeId` joins `creatives[].id`. Colorway rows
// use `colorway`. A carousel uses `colorwayDisplayList`, then
// `colorwayList`, then `colorways`. Video and static rows with no
// colorway omit that line. A budget cell's `spendClass` puts weekly and
// flight in New or Existing spend, and the other column is 0. Test rows
// whose weekly and flight are objects use newSpend and existingSpend.
// 0 is shown as zero. When `incrementalRevenue` equals `revenue`, the
// forecast notes that every cell is new spend.
//
// The older `{ campaigns }` object and a top-level `campaigns` list are fallbacks.
// `budget.cells` are the cell rows when `lines` is absent. A cell list
// with no spend class and no object weekly/flight stays the older table.
// Forecast numbers are
// read from `forecast.total` first; `incrementalRevenue` is the sales
// impact. Older forecasts keep top-level numbers and `incrementalSales`.
// Coverage gaps may also arrive as `coverageGaps` strings or
// `{ subject, needed }`. `coverage.unavailableReason` replaces the gap
// list and the suggested builds.
//
// `suggestions` is the list the backend sends. `suggestedBuilds` and
// `builds` are read only when `suggestions` is absent. A row with no
// tool is the older ad-build shape and still opens ad generation.
// `catalog.generateLifestyleImages` opens Image Studio. A row whose
// tool or endpoint is POST /api/ads/generate (or ads.generate) opens
// the ad wizard. Product titles are not ids; productIds are used when
// the row or its product objects carry them.
//
// Null measures stay null. They are not coerced to 0.

import { apiJson } from '../auth/apiFetch';
import { formatMoney, isMissingEndpoint, isSecretText, publicText, safeText } from './client';

export type PlanStatusChip = 'planned' | 'created_paused' | 'live' | 'other';
export type PlanSource = 'reach_social' | 'existing';

export type PlanTargetingView = {
  geo: string | null;
  ages: string | null;
  gender: string | null;
  exclusions: string | null;
  advantageAudience: 'On' | 'Off' | null;
  placements: string | null;
};

export type PlanCreativeView = {
  id: string | null;
  name: string | null;
  type: string | null;
  colorway: string | null;
  exists: boolean | null;
  statusLabel: string | null;
  source: PlanSource | null;
  copyApproval: string | null;
  reachSocialCreated: boolean | null;
};

export type PlanAdView = {
  name: string;
  status: PlanStatusChip;
  statusLabel: string | null;
  source: PlanSource | null;
  creativeIds: string[];
  creatives: PlanCreativeView[];
};

export type PlanAdSetView = {
  name: string;
  status: PlanStatusChip;
  statusLabel: string | null;
  source: PlanSource | null;
  targeting: PlanTargetingView;
  ads: PlanAdView[];
};

export type PlanCampaignNode = {
  name: string;
  action: string | null;
  status: PlanStatusChip;
  statusLabel: string | null;
  source: PlanSource | null;
  adSets: PlanAdSetView[];
};

export type PlanBudgetCell = {
  name: string;
  weekly: string | null;
  flight: string | null;
  newSpend: string | null;
  existingSpend: string | null;
};

export type PlanMeasure = {
  value: string | null;
  reason: string | null;
};

export type PlanAdKinds = 'image' | 'video' | 'both';
export type PlanBuildRoute = 'ads' | 'lifestyle';

export type PlanSuggestedBuild = {
  action: string;
  estimatedCostUsd: number | null;
  estimatedCost: string | null;
  productIds: string[];
  campaignId: string | null;
  tool: string | null;
  args: Record<string, unknown> | null;
  kinds: PlanAdKinds | null;
  planLineKey: string | null;
  route: PlanBuildRoute | null;
  creativeType: string | null;
  generateCount: number | null;
  needsLineCampaign: boolean;
};

export type PlanStudio = {
  title: string;
  status: string | null;
  revision: string | null;
  currency: string | null;
  campaigns: PlanCampaignNode[];
  budget: PlanBudgetCell[];
  forecast: {
    spend: PlanMeasure;
    purchases: PlanMeasure;
    cpa: PlanMeasure;
    roas: PlanMeasure;
    revenue: PlanMeasure;
    incremental: PlanMeasure;
    incrementalNote: string | null;
    method: string | null;
  };
  gaps: string[];
  coverageUnavailable: string | null;
  creatives: PlanCreativeView[];
  builds: PlanSuggestedBuild[];
};

export type PlanStudioResult = {
  detail: PlanStudio | null;
  missing: boolean;
  error: string | null;
};

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

function text(value: unknown, limit = 200): string | null {
  return safeText(value, limit);
}

function currencyCode(value: unknown): string | null {
  const code = str(value);
  if (!code || !/^[A-Za-z]{3}$/.test(code)) return null;
  return code.toUpperCase();
}

function requestId(value: unknown): string | null {
  const id = str(value);
  if (!id || id.length > 80 || isSecretText(id)) return null;
  return id;
}

function joinValues(value: unknown): string | null {
  if (Array.isArray(value)) {
    const parts = value.map((item) => joinValues(item)).filter((item): item is string => Boolean(item));
    return parts.length ? parts.join(', ') : null;
  }
  if (isRec(value)) {
    const min = num(value.min ?? value.from);
    const max = num(value.max ?? value.to);
    if (min != null && max != null) return `${min}–${max}`;
    return text(value.label ?? value.name ?? value.text ?? value.summary, 200);
  }
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return text(value, 200);
}

export function readStatus(value: unknown): { chip: PlanStatusChip; label: string | null } {
  const raw = text(value, 40);
  if (!raw) return { chip: 'other', label: null };
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'planned' || key === 'draft' || key === 'proposed') return { chip: 'planned', label: 'Planned' };
  if (key === 'created paused' || key === 'paused') return { chip: 'created_paused', label: 'Created paused' };
  if (key === 'live' || key === 'active') return { chip: 'live', label: 'Live' };
  return { chip: 'other', label: raw };
}

export function readSource(value: unknown): PlanSource | null {
  const raw = text(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'reach social' || key === 'rs' || key === 'new') return 'reach_social';
  if (key === 'existing' || key === 'meta') return 'existing';
  return null;
}

function presentedStatus(value: Rec): { chip: PlanStatusChip; label: string | null } {
  const raw = value.status;
  const missing = raw == null || (typeof raw === 'string' && !raw.trim());
  if (missing) {
    const reason = text(value.statusReason, 240);
    if (reason) return { chip: 'other', label: reason };
    return { chip: 'other', label: null };
  }
  return readStatus(raw);
}

function readAction(value: unknown): string | null {
  const raw = text(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-\s]+/g, '');
  if (key === 'create') return 'Create';
  if (key === 'mirrornew') return 'Mirror new';
  if (key === 'reuse') return 'Reuse';
  return raw;
}

function labelize(value: string | null): string | null {
  if (!value) return null;
  if (value.length > 48) return value;
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function readTargeting(value: unknown): PlanTargetingView {
  const record = isRec(value) ? value : {};
  const advantage = record.advantageAudience ?? record.advantage;
  let advantageAudience: 'On' | 'Off' | null = null;
  if (typeof advantage === 'boolean') advantageAudience = advantage ? 'On' : 'Off';
  else if (typeof advantage === 'string') {
    const key = advantage.trim().toLowerCase();
    if (key === 'on' || key === 'true' || key === 'yes') advantageAudience = 'On';
    else if (key === 'off' || key === 'false' || key === 'no') advantageAudience = 'Off';
  }
  const ages = record.ages ?? record.age ?? (
    record.ageMin != null || record.ageMax != null
      ? { min: record.ageMin, max: record.ageMax }
      : null
  );
  return {
    geo: joinValues(record.geo ?? record.geos ?? record.countries ?? record.locations),
    ages: joinValues(ages),
    gender: joinValues(record.gender ?? record.genders),
    exclusions: joinValues(record.exclusions ?? record.excluded),
    advantageAudience,
    placements: joinValues(record.placements ?? record.placement)
  };
}

function joinedList(value: unknown): string | null {
  if (Array.isArray(value)) {
    const parts = value.map((item) => {
      if (isRec(item)) return text(item.display ?? item.label ?? item.name ?? item.colorway ?? item.title, 80);
      return text(item, 80);
    }).filter((item): item is string => Boolean(item));
    return parts.length ? text(parts.join(', '), 400) : null;
  }
  return text(value, 400);
}

function colorwayOf(value: Rec, id: string | null, type: string | null): string | null {
  const typeKey = (type ?? '').toLowerCase();
  const carousel = typeKey.includes('carousel') || (id ?? '').toLowerCase() === 'carousel';
  if (carousel) {
    return joinedList(value.colorwayDisplayList ?? value.colorwayDisplay ?? value.displayColorways)
      ?? joinedList(value.colorwayList)
      ?? joinedList(value.colorways);
  }
  return text(value.colorway ?? value.color, 80);
}

function readCreative(value: Rec): PlanCreativeView | null {
  const existsRaw = value.exists ?? value.state ?? value.presence;
  let exists: boolean | null = null;
  if (typeof existsRaw === 'boolean') exists = existsRaw;
  else if (typeof existsRaw === 'string') {
    const key = existsRaw.trim().toLowerCase();
    if (key === 'exists' || key === 'present') exists = true;
    else if (key === 'missing' || key === 'absent') exists = false;
  }
  const statusLabel = labelize(text(value.status, 80));
  if (exists == null && statusLabel) {
    const key = statusLabel.toLowerCase();
    if (key === 'missing' || key === 'absent') exists = false;
    else if (key === 'exists' || key === 'present' || key === 'ready') exists = true;
  }
  const approvalRaw = text(value.copyApproval ?? value.copyStatus ?? value.approval, 40);
  const approvalKey = approvalRaw?.toLowerCase().replace(/[_-]+/g, ' ');
  const approval = approvalKey === 'approved' ? 'Approved'
    : approvalKey === 'pending' ? 'Pending'
    : approvalKey === 'rejected' ? 'Rejected'
    : approvalRaw;
  const id = text(value.id ?? value.creativeId ?? value.key, 80);
  const type = text(value.type ?? value.kind ?? value.format, 40);
  const creative: PlanCreativeView = {
    id,
    name: text(value.name ?? value.title ?? value.label, 160),
    type,
    colorway: colorwayOf(value, id, type),
    exists,
    statusLabel,
    source: readSource(value.source ?? value.origin),
    copyApproval: approval ? approval.replace(/[_-]+/g, ' ') : null,
    reachSocialCreated: reachSocialFlag(value)
  };
  if (!creative.id && !creative.name && !creative.type && !creative.colorway && creative.exists == null && !creative.statusLabel && !creative.source && !creative.copyApproval && creative.reachSocialCreated == null) {
    return null;
  }
  return creative;
}

function reachSocialFlag(value: Rec): boolean | null {
  const raw = value.reachSocialCreated ?? value.reachSocial ?? value.createdByReachSocial ?? value.rsCreated;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const key = raw.trim().toLowerCase();
    if (key === 'true' || key === 'yes' || key === 'reach social' || key === 'reach_social') return true;
    if (key === 'false' || key === 'no') return false;
  }
  if (readSource(value.source ?? value.origin) === 'reach_social') return true;
  return null;
}

function creativeIdsOf(value: Rec): string[] {
  const raw = value.creativeId ?? value.creative_id ?? value.creativeIds;
  const list = Array.isArray(raw) ? raw : [raw];
  const ids: string[] = [];
  for (const item of list) {
    const id = text(item, 80);
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

function readAd(value: Rec, index: number): PlanAdView {
  const status = presentedStatus(value);
  return {
    name: text(value.name ?? value.title ?? value.label, 160) ?? `Ad ${index + 1}`,
    status: status.chip,
    statusLabel: status.label,
    source: readSource(value.source ?? value.origin),
    creativeIds: creativeIdsOf(value),
    creatives: records(value.creatives ?? value.creative).map(readCreative).filter((row): row is PlanCreativeView => row != null)
  };
}

function readAdSet(value: Rec, index: number): PlanAdSetView {
  const status = presentedStatus(value);
  return {
    name: text(value.name ?? value.title ?? value.label, 160) ?? `Ad set ${index + 1}`,
    status: status.chip,
    statusLabel: status.label,
    source: readSource(value.source ?? value.origin),
    targeting: readTargeting(value.targeting),
    ads: records(value.ads ?? value.ad).map(readAd)
  };
}

function readCampaign(value: Rec, index: number): PlanCampaignNode {
  const status = presentedStatus(value);
  return {
    name: text(value.name ?? value.title ?? value.label, 160) ?? `Campaign ${index + 1}`,
    action: readAction(value.action),
    status: status.chip,
    statusLabel: status.label,
    source: readSource(value.source ?? value.origin),
    adSets: records(value.adSets ?? value.adsets ?? value.ad_sets).map(readAdSet)
  };
}

function moneyOf(value: unknown, currency: string | null): string | null {
  const amount = num(value);
  if (amount == null) return null;
  return formatMoney(amount, currency);
}

function spendClassOf(value: unknown): 'new' | 'existing' | null {
  const raw = text(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'new') return 'new';
  if (key === 'existing') return 'existing';
  return null;
}

function spendParts(value: unknown): { newSpend: number | null; existingSpend: number | null } | null {
  if (!isRec(value)) return null;
  const hasNew = 'newSpend' in value || 'new' in value;
  const hasExisting = 'existingSpend' in value || 'existing' in value;
  if (!hasNew && !hasExisting) return null;
  return {
    newSpend: hasNew ? num(value.newSpend ?? value.new) : null,
    existingSpend: hasExisting ? num(value.existingSpend ?? value.existing) : null
  };
}

function moneyText(amount: number | null, currency: string | null): string | null {
  if (amount == null) return null;
  return formatMoney(amount, currency);
}

function classifiedSpend(weekly: number | null, flight: number | null, currency: string | null): string | null {
  const parts = [
    weekly == null ? null : `${formatMoney(weekly, currency)} weekly`,
    flight == null ? null : `${formatMoney(flight, currency)} flight`
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(' · ') : null;
}

function partTotal(parts: { newSpend: number | null; existingSpend: number | null } | null): number | null {
  if (!parts || (parts.newSpend == null && parts.existingSpend == null)) return null;
  return (parts.newSpend ?? 0) + (parts.existingSpend ?? 0);
}

function budgetRow(row: Rec, index: number, fallback: string, currency: string | null): PlanBudgetCell {
  const name = text(row.name ?? row.label ?? row.line ?? row.test ?? row.cell, 160) ?? `${fallback} ${index + 1}`;
  const weeklyParts = spendParts(row.weekly);
  const flightParts = spendParts(row.flight);
  if (weeklyParts || flightParts) {
    return {
      name,
      weekly: moneyText(partTotal(weeklyParts), currency),
      flight: moneyText(partTotal(flightParts), currency),
      newSpend: classifiedSpend(weeklyParts?.newSpend ?? null, flightParts?.newSpend ?? null, currency),
      existingSpend: classifiedSpend(weeklyParts?.existingSpend ?? null, flightParts?.existingSpend ?? null, currency)
    };
  }
  const weeklyAmount = num(row.weekly ?? row.weeklyTotal ?? row.weeklyBudget);
  const flightAmount = num(row.flight ?? row.flightTotal ?? row.total);
  const spendClass = spendClassOf(row.spendClass);
  if (spendClass && (weeklyAmount != null || flightAmount != null)) {
    const packed = classifiedSpend(weeklyAmount, flightAmount, currency) ?? formatMoney(0, currency);
    const zero = formatMoney(0, currency);
    return {
      name,
      weekly: moneyText(weeklyAmount, currency),
      flight: moneyText(flightAmount, currency),
      newSpend: spendClass === 'new' ? packed : zero,
      existingSpend: spendClass === 'existing' ? packed : zero
    };
  }
  return {
    name,
    weekly: moneyText(weeklyAmount, currency),
    flight: moneyText(flightAmount, currency),
    newSpend: moneyOf(row.newSpend ?? row.new ?? row.reachSocialSpend, currency),
    existingSpend: moneyOf(row.existingSpend ?? row.existing, currency)
  };
}

function readBudget(value: unknown, currency: string | null): PlanBudgetCell[] {
  const root = isRec(value) ? value : {};
  const lines = records(root.lines);
  const tests = records(root.tests);
  const cells = records(isRec(value) ? (root.cells ?? root.rows) : value);
  const rootWeekly = num(root.weekly);
  const rootFlight = num(root.flight);
  const splitCells = cells.some((row) => spendClassOf(row.spendClass) != null || spendParts(row.weekly) != null || spendParts(row.flight) != null);
  if (rootWeekly != null || rootFlight != null || lines.length || tests.length || splitCells) {
    const rows: PlanBudgetCell[] = [];
    if (rootWeekly != null || rootFlight != null) {
      rows.push({
        name: 'Plan total',
        weekly: moneyText(rootWeekly, currency),
        flight: moneyText(rootFlight, currency),
        newSpend: null,
        existingSpend: null
      });
    }
    const cellRows = lines.length ? lines : cells;
    cellRows.forEach((row, index) => rows.push(budgetRow(row, index, lines.length ? 'Line' : 'Cell', currency)));
    tests.forEach((row, index) => rows.push(budgetRow(row, index, 'Test', currency)));
    return rows;
  }
  return cells.map((row, index) => budgetRow(row, index, 'Cell', currency));
}

function measure(value: unknown, reason: unknown, currency: string | null, kind: 'money' | 'count' | 'roas'): PlanMeasure {
  const amount = num(value);
  let rendered: string | null = null;
  if (amount != null && kind === 'money') rendered = formatMoney(amount, currency);
  if (amount != null && kind === 'count') {
    rendered = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);
  }
  if (amount != null && kind === 'roas') {
    rendered = `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}×`;
  }
  return { value: rendered, reason: rendered == null ? text(reason, 240) : null };
}

function readForecast(value: unknown, currency: string | null): PlanStudio['forecast'] {
  const record = isRec(value) ? value : {};
  const total = isRec(record.total) ? record.total : null;
  const source = total ?? record;
  const reasons = isRec(source.reasons) ? source.reasons : (isRec(record.reasons) ? record.reasons : {});
  const reason = (key: string) => source[`${key}Reason`] ?? record[`${key}Reason`] ?? reasons[key] ?? source[`${key}Unavailable`] ?? record[`${key}Unavailable`];
  const incremental = isRec(source.incremental) ? source.incremental : (isRec(record.incremental) ? record.incremental : null);
  const incrementalValue = total && ('incrementalRevenue' in total || 'incrementalSales' in total)
    ? (total.incrementalRevenue ?? total.incrementalSales)
    : (source.incrementalRevenue ?? record.incrementalRevenue ?? source.incrementalSales ?? record.incrementalSales ?? incremental?.revenue ?? incremental?.sales);
  const revenueAmount = num(source.revenue !== undefined ? source.revenue : record.revenue);
  const incrementalRevenueAmount = total && 'incrementalRevenue' in total ? num(total.incrementalRevenue) : num(record.incrementalRevenue);
  const incrementalNote = incrementalRevenueAmount != null && revenueAmount != null && incrementalRevenueAmount === revenueAmount
    ? 'All cells are new spend, so incremental equals revenue.'
    : null;
  return {
    spend: measure(source.spend, reason('spend'), currency, 'money'),
    purchases: measure(source.purchases, reason('purchases'), currency, 'count'),
    cpa: measure(source.cpa, reason('cpa'), currency, 'money'),
    roas: measure(source.roas, reason('roas'), currency, 'roas'),
    revenue: measure(source.revenue, reason('revenue'), currency, 'money'),
    incremental: measure(
      incrementalValue,
      source.incrementalRevenueReason ?? record.incrementalRevenueReason ?? source.incrementalSalesReason ?? record.incrementalSalesReason ?? incremental?.reason ?? reason('incrementalSales'),
      currency,
      'money'
    ),
    incrementalNote,
    method: text(source.method ?? record.method ?? source.assumptions ?? record.assumptions ?? source.note ?? record.note, 800)
  };
}

function attachCreatives(campaigns: PlanCampaignNode[], catalog: PlanCreativeView[]): void {
  const byId = new Map<string, PlanCreativeView>();
  for (const creative of catalog) {
    if (creative.id) byId.set(creative.id, creative);
  }
  if (!byId.size) return;
  for (const campaign of campaigns) {
    for (const adSet of campaign.adSets) {
      for (const ad of adSet.ads) {
        if (!ad.creativeIds.length) continue;
        ad.creatives = ad.creativeIds.flatMap((id) => {
          const found = byId.get(id);
          return found ? [found] : [];
        });
      }
    }
  }
}

function productLabel(value: unknown): string | null {
  const parts = (Array.isArray(value) ? value : [value]).map((item) => {
    if (isRec(item)) return text(item.title ?? item.name ?? item.label, 80);
    return text(item, 80);
  }).filter((item): item is string => Boolean(item));
  if (!parts.length) return null;
  return text(parts.join(', '), 200);
}

function countLabel(value: unknown, suffix: string): string | null {
  const amount = num(value);
  if (amount == null) return null;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount)} ${suffix}`;
}

function readGap(value: unknown): string | null {
  const ready = text(value, 300);
  if (ready) return ready;
  if (!isRec(value)) return null;
  const sentence = text(value.text ?? value.summary ?? value.message ?? value.label, 300);
  if (sentence) return sentence;
  const line = text(value.line, 120);
  const creativeType = text(value.creativeType, 40);
  const products = productLabel(value.products);
  if (line || creativeType || products || num(value.required) != null) {
    const label = [line, creativeType, products].filter((part): part is string => Boolean(part)).join(' · ');
    const counts = [countLabel(value.required, 'required'), countLabel(value.missing, 'missing')].filter((part): part is string => Boolean(part)).join(', ');
    if (label && counts) return text(`${label}: ${counts}`, 300);
    return text(label || counts, 300);
  }
  const subject = text(value.subject ?? value.name, 120);
  const needed = text(value.needed ?? value.need, 80);
  if (!subject || !needed) return null;
  const exists = text(value.existsNote ?? value.existsLabel, 80);
  if (exists) return `${subject} needs ${needed}; ${exists}`;
  const missing = num(value.missing);
  if (missing != null) return `${subject} needs ${needed}; ${missing} missing`;
  const missingText = text(value.missing, 80);
  if (missingText) return `${subject} needs ${needed}; ${missingText}`;
  return `${subject} needs ${needed}`;
}

const LIFESTYLE_TOOL = 'catalog.generateLifestyleImages';

function normalizeTool(value: unknown): string | null {
  const raw = str(value);
  if (!raw || raw.length > 160 || isSecretText(raw)) return null;
  return raw.replace(/__/g, '.');
}

function mentionsAdsGenerate(value: string): boolean {
  return value === 'ads.generate'
    || value === 'ads.generateAds'
    || /\/api\/ads\/generate\b/.test(value);
}

function pointsAtAdsGenerate(tool: string | null, args: Rec | null, value: Rec): boolean {
  const candidates = [
    tool,
    str(value.endpoint),
    str(value.path),
    str(args?.endpoint),
    str(args?.path)
  ].filter((item): item is string => Boolean(item));
  return candidates.some(mentionsAdsGenerate);
}

export function readAdKinds(value: unknown): PlanAdKinds | null {
  const tokens = (Array.isArray(value) ? value : [value])
    .flatMap((item) => str(item)?.toLowerCase().split(/[\s,]+/) ?? []);
  const image = tokens.some((token) => token === 'image' || token === 'static');
  const video = tokens.some((token) => token === 'video');
  const both = tokens.some((token) => token === 'both');
  if (both || (image && video)) return 'both';
  if (video) return 'video';
  if (image) return 'image';
  return null;
}

function routeFor(tool: string | null, args: Rec | null, value: Rec): PlanBuildRoute | null {
  if (tool === LIFESTYLE_TOOL) return 'lifestyle';
  const candidates = [
    tool,
    str(value.endpoint),
    str(value.path),
    str(args?.endpoint),
    str(args?.path)
  ].filter((item): item is string => Boolean(item));
  if (candidates.some(mentionsAdsGenerate)) return 'ads';
  if (!tool) return 'ads';
  return null;
}

function readArgs(value: unknown): Rec | null {
  if (!isRec(value)) return null;
  const out: Rec = {};
  for (const [key, raw] of Object.entries(value)) {
    if (isSecretText(key)) continue;
    if (typeof raw === 'string') {
      if (!isSecretText(raw)) out[key] = raw;
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = raw;
    } else if (Array.isArray(raw)) {
      const items: unknown[] = [];
      for (const item of raw) {
        if (typeof item === 'string') {
          if (!isSecretText(item)) items.push(item);
        } else if (typeof item === 'number' || typeof item === 'boolean') {
          items.push(item);
        } else if (isRec(item)) {
          const nested = readArgs(item);
          if (nested) items.push(nested);
        }
      }
      if (items.length) out[key] = items;
    } else if (isRec(raw)) {
      const nested = readArgs(raw);
      if (nested) out[key] = nested;
    }
  }
  return Object.keys(out).length ? out : null;
}

function pushProductId(ids: string[], seen: Set<string>, value: unknown) {
  const id = requestId(value);
  if (!id || seen.has(id)) return;
  seen.add(id);
  ids.push(id);
}

function idsFromList(value: unknown, ids: string[], seen: Set<string>, allowBareString: boolean) {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    if (isRec(item)) pushProductId(ids, seen, item.productId ?? item.id ?? item._id);
    else if (allowBareString) pushProductId(ids, seen, item);
  }
}

function readHandoff(value: Rec, args: Rec | null): Rec | null {
  if (isRec(value.handoff)) return value.handoff;
  if (isRec(args?.handoff)) return args.handoff;
  return null;
}

function readProductIds(value: Rec, args: Rec | null, handoff: Rec | null): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  idsFromList(value.productIds, ids, seen, true);
  idsFromList(args?.productIds, ids, seen, true);
  idsFromList(handoff?.productIds, ids, seen, true);
  idsFromList(value.products, ids, seen, false);
  idsFromList(args?.products, ids, seen, false);
  idsFromList(handoff?.products, ids, seen, false);
  return ids;
}

function readGenerateCount(handoff: Rec | null): number | null {
  if (!handoff) return null;
  const units = handoff.units;
  if (typeof units === 'number' && Number.isInteger(units) && units > 0) return units;
  if (typeof units === 'string' && /^\d+$/.test(units.trim())) {
    const parsed = Number(units);
    if (parsed > 0) return parsed;
  }
  if (Array.isArray(units) && units.length > 0) return units.length;
  return null;
}

function readCreativeType(value: Rec, args: Rec | null, handoff: Rec | null): string | null {
  const raw = str(args?.creativeType ?? value.creativeType ?? handoff?.creativeType);
  if (!raw) return null;
  const slug = raw.toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || slug.length > 40) return null;
  return slug;
}

function readLineKey(value: Rec, args: Rec | null, handoff: Rec | null): string | null {
  return requestId(
    value.planLineKey ?? value.lineKey ?? args?.planLineKey ?? args?.lineKey
    ?? handoff?.planLineKey ?? handoff?.lineKey
  );
}

function rememberLine(map: Map<string, string | null>, key: unknown, campaignId: unknown) {
  const line = requestId(key);
  const campaign = requestId(campaignId);
  if (!line || !campaign) return;
  const prev = map.get(line);
  if (prev === undefined) map.set(line, campaign);
  else if (prev !== campaign) map.set(line, null);
}

function walkLineRows(map: Map<string, string | null>, rows: unknown, inheritedCampaign?: unknown) {
  for (const row of records(rows)) {
    rememberLine(map, row.planLineKey ?? row.lineKey ?? row.localKey ?? row.key, row.campaignId ?? inheritedCampaign);
    const nested = row.lines ?? row.planLines;
    if (Array.isArray(nested)) walkLineRows(map, nested, row.campaignId ?? row.id ?? inheritedCampaign);
  }
}

function lineCampaignsOf(body: Rec): Map<string, string> {
  const draft = new Map<string, string | null>();
  walkLineRows(draft, body.lines);
  walkLineRows(draft, body.planLines);
  const structure = isRec(body.structure) ? body.structure : null;
  if (structure) walkLineRows(draft, structure.campaigns);
  if (Array.isArray(body.campaigns)) walkLineRows(draft, body.campaigns);
  if (isRec(body.campaigns)) {
    walkLineRows(draft, body.campaigns.reused);
    walkLineRows(draft, body.campaigns.created);
  }
  const sections = isRec(body.sections) ? body.sections : null;
  const sectionLines = sections && isRec(sections.lines) ? sections.lines : null;
  if (sectionLines) {
    walkLineRows(draft, sectionLines.reuse);
    walkLineRows(draft, sectionLines.create);
    walkLineRows(draft, sectionLines.unbound);
  }
  const out = new Map<string, string>();
  for (const [key, campaign] of draft) {
    if (campaign) out.set(key, campaign);
  }
  return out;
}

function readBuild(value: Rec, currency: string | null, lineCampaigns: Map<string, string>): PlanSuggestedBuild | null {
  const tool = normalizeTool(value.tool ?? value.toolName);
  const args = readArgs(value.args);
  const handoff = readHandoff(value, args);
  const creativeType = readCreativeType(value, args, handoff);
  let route = routeFor(tool, args, value);
  if (creativeType === 'catalog-image' && !pointsAtAdsGenerate(tool, args, value)) route = 'lifestyle';
  const action = text(value.action ?? value.label ?? value.name ?? value.title ?? value.summary, 200)
    ?? (route === 'lifestyle' ? 'Generate lifestyle images' : route === 'ads' ? 'Generate ads' : tool);
  if (!action) return null;
  const estimated = num(value.estimatedCostUsd ?? value.estimatedUsd ?? value.estimatedCost ?? args?.estimatedCostUsd);
  const rowCurrency = currencyCode(value.currency) ?? currency;
  const ownCampaign = requestId(value.campaignId ?? args?.campaignId ?? handoff?.campaignId);
  const planLineKey = readLineKey(value, args, handoff);
  const fromLine = !ownCampaign && planLineKey ? (lineCampaigns.get(planLineKey) ?? null) : null;
  const campaignId = ownCampaign ?? fromLine;
  return {
    action,
    estimatedCostUsd: estimated != null && estimated >= 0 ? estimated : null,
    estimatedCost: estimated != null ? formatMoney(estimated, rowCurrency) : null,
    productIds: readProductIds(value, args, handoff),
    campaignId,
    tool,
    args,
    kinds: readAdKinds(value.kinds ?? value.adKinds ?? value.kind ?? args?.kinds ?? args?.adKinds ?? args?.kind)
      ?? (creativeType === 'static-ad' ? 'image' : creativeType === 'video-ad' ? 'video' : null),
    planLineKey,
    route,
    creativeType,
    generateCount: readGenerateCount(handoff),
    needsLineCampaign: route === 'ads' && !campaignId && Boolean(planLineKey)
  };
}

export function suggestionHref(
  build: PlanSuggestedBuild,
  capUsd: number,
  plan: { planId: string; revision: string | null }
): string | null {
  if (build.route !== 'ads' && build.route !== 'lifestyle') return null;
  const params = new URLSearchParams();
  if (build.route === 'ads' && build.campaignId) params.set('campaignId', build.campaignId);
  if (build.productIds.length) params.set('productIds', build.productIds.join(','));
  if (build.route === 'ads' && build.kinds) params.set('kinds', build.kinds);
  if (build.creativeType) params.set('creativeType', build.creativeType);
  if (build.generateCount != null) params.set('generateCount', String(build.generateCount));
  if (build.needsLineCampaign) params.set('lineCampaign', 'required');
  if (plan.planId) params.set('planId', plan.planId);
  if (build.planLineKey) params.set('planLineKey', build.planLineKey);
  if (plan.revision) params.set('revision', plan.revision);
  params.set('capUsd', String(capUsd));
  const path = build.route === 'lifestyle' ? '/image-studio' : '/generate-ads';
  return `${path}?${params.toString()}`;
}

function campaignsOf(body: Rec): PlanCampaignNode[] {
  if (Array.isArray(body.structure) && body.structure.length) return records(body.structure).map(readCampaign);
  const structure = isRec(body.structure) ? body.structure : null;
  const rows = structure && records(structure.campaigns).length ? structure.campaigns : body.campaigns;
  return records(rows).map(readCampaign);
}

function revisionOf(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return text(value, 40);
}

export function readPlanStudio(payload: unknown): PlanStudio | null {
  if (!isRec(payload)) return null;
  const body = isRec(payload.detail) ? payload.detail : payload;
  const currency = currencyCode(body.currency) ?? currencyCode(isRec(body.budget) ? body.budget.currency : null);
  const campaigns = campaignsOf(body);
  const budget = readBudget(body.budget, currency);
  const forecast = readForecast(body.forecast, currency);
  const coverage = isRec(body.coverage) ? body.coverage : null;
  const coverageUnavailable = text(coverage?.unavailableReason, 400);
  const gapSource = coverage && Array.isArray(coverage.gaps) ? coverage.gaps : body.coverageGaps;
  const gaps = coverageUnavailable ? [] : (Array.isArray(gapSource) ? gapSource : []).map(readGap).filter((gap): gap is string => Boolean(gap));
  const creatives = records(body.creatives).map(readCreative).filter((row): row is PlanCreativeView => row != null);
  attachCreatives(campaigns, creatives);
  const buildSource = coverageUnavailable ? [] : (Array.isArray(body.suggestions) ? body.suggestions : (body.suggestedBuilds ?? body.builds));
  const lineCampaigns = lineCampaignsOf(body);
  const builds = records(buildSource).map((row) => readBuild(row, currency, lineCampaigns)).filter((row): row is PlanSuggestedBuild => row != null);
  const title = publicText(body.title ?? body.name, 160);
  const status = text(body.status, 40);
  const revision = revisionOf(body.revision);
  const forecastPresent = forecast.method != null || [
    forecast.spend, forecast.purchases, forecast.cpa, forecast.roas, forecast.revenue, forecast.incremental
  ].some((item) => item.value != null || item.reason != null);
  if (!title && !status && !revision && !campaigns.length && !budget.length && !gaps.length && !builds.length && !forecastPresent && !creatives.length && !coverageUnavailable) return null;
  return {
    title: title ?? 'Marketing plan',
    status,
    revision,
    currency,
    campaigns,
    budget,
    forecast,
    gaps,
    coverageUnavailable,
    creatives,
    builds
  };
}

/** A query brand is not the active brand. Null active is a mismatch, same as an explicit other brand. */
export function planBrandMismatch(queryBrand: string | null, activeId: string | null): boolean {
  return Boolean(queryBrand && queryBrand !== activeId);
}

/** App route for one plan. brandId and revision stay in the query so a brand switch cannot retarget the open plan. */
export function planDetailHref(brandId: string | null, planId: string, revision?: string | null): string {
  const params = new URLSearchParams();
  if (brandId) params.set('brandId', brandId);
  if (revision) params.set('revision', revision);
  const query = params.toString();
  return `/plans/${encodeURIComponent(planId)}${query ? `?${query}` : ''}`;
}

/** Keep /v2 on plan links while the operator is already inside /v2/plans. */
export function planHrefForLocation(href: string, pathname: string): string {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const onPlansV2 = path === '/v2/plans' || path.startsWith('/v2/plans/');
  if (!onPlansV2 || !href.startsWith('/plans/')) return href;
  return `/v2${href}`;
}

export function planDetailUrl(brandId: string, planId: string, revision?: string | null): string {
  const params = new URLSearchParams({ brandId });
  if (revision) params.set('revision', revision);
  return `/api/marketing/plans/${encodeURIComponent(planId)}/detail?${params}`;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message && !isSecretText(err.message)) return err.message;
  return 'Could not load this plan.';
}

export async function getPlanDetail(brandId: string, planId: string, revision?: string | null): Promise<PlanStudioResult> {
  try {
    const body = await apiJson<unknown>(planDetailUrl(brandId, planId, revision), { cache: 'no-store' });
    const detail = readPlanStudio(body);
    if (!detail) return { detail: null, missing: true, error: null };
    return { detail, missing: false, error: null };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return { detail: null, missing: true, error: null };
    return { detail: null, missing: false, error: errorMessage(err) };
  }
}
